import i18next from 'i18next';
import { toast } from 'sonner';
import { useHomeStore } from '@/store/home.store.js';
import { useTenantStore } from '@/store/tenant.store.js';

export class HomeIntegration {
    constructor(sdkService) {
        this.service = sdkService;
        /** Microtask batch: sections requested by widgets mounting in the same tick. */
        this._pendingSections = new Set();
        this._flushScheduled = false;
        /** Sections currently being fetched (per tenant scope). */
        this._inflightSections = new Set();
        this._tenantScope = null;
    }

    /**
     * Widget provider entry point. Requests from widgets mounting together are
     * batched into a single `GET /home?sections=…` call; sections already
     * in-flight are not requested twice.
     */
    ensureSection(section) {
        const tenantScope = String(useTenantStore.getState().selectedTenantId ?? '');
        if (this._tenantScope !== tenantScope) {
            this._tenantScope = tenantScope;
            this._inflightSections.clear();
            this._pendingSections.clear();
        }
        if (this._inflightSections.has(section)) return;
        this._pendingSections.add(section);
        if (this._flushScheduled) return;
        this._flushScheduled = true;
        queueMicrotask(() => {
            this._flushScheduled = false;
            const batch = [...this._pendingSections].filter((s) => !this._inflightSections.has(s));
            this._pendingSections.clear();
            if (batch.length === 0) return;
            this.fetchSummary(batch);
        });
    }

    /** Fetch the given summary sections and merge them into the store. */
    async fetchSummary(sections = []) {
        const store = useHomeStore.getState();
        for (const section of sections) this._inflightSections.add(section);
        store.setLoading(true);
        store.setError(null);

        try {
            const response = await this.service.getSummary(sections);
            if (response.success && response.data) {
                useHomeStore.getState().mergeSections(response.data);
                return { success: true, data: response.data };
            }
            const message = i18next.t('integrationToasts:couldNotLoadHomeData', { defaultValue: 'Could not load home data' });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:couldNotLoadHomeData', { defaultValue: 'Could not load home data' });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            for (const section of sections) this._inflightSections.delete(section);
            useHomeStore.getState().setLoading(false);
        }
    }
}
